/* eslint-disable */
import { Col, FormProps, message, Row, Spin, Typography } from 'antd';
import produce from 'immer';
import { get, isEmpty, set, sortBy, unset } from 'lodash';
import React, { FC, useEffect, useState, useRef } from 'react';
import { useMediaQuery } from 'react-responsive';
import styled from 'styled-components';
import { useDebouncedCallback } from 'use-debounce/lib';
import { CardMobile, Collapse, Space } from '~/components';
import SaveWizardStep from '~/components/SaveWizardStep';
import Form from '~/components/WizardForm';
import colors from '~/constants/colors';

import theme from '~/constants/theme';
import {
  useApplyCustomPricingController,
  useCustomPricingDrawerOpen,
  useFieldMap,
  useGetServices,
} from '~/controllers/pricing';
import { ServicesContextProvider } from '~/controllers/ServicesContext';
import { useTotalPriceController } from '~/controllers/totalPrice';
import {
  useGetApplication,
  useIsAtLeastOneServiceSelected,
  useSelectStep,
  useSubmitStep,
} from '~/controllers/wizard';
import { useMutableCallback } from '~/hooks';
import { ApplicationPayload } from '~/types/ApplicationPayload';
import { CustomPricing, ProductMap } from '~/types/CustomPricing';
import { ServicesKeys, PricingServiceField } from '~/types/Services';
import buildNamePath from '~/utils/buildNamePath';
import FeesAndChargesFields from './FeesAndChargesFields';
import NumberOfDevicesFormItem from './NumberOfDevicesFormItem';
import { SAVE_STEP_FORM } from './RequestCustomPricing';
import { servicesFormName, servicesStepKey } from './SelectServices';
import ServiceCheckbox from './ServiceCheckbox';
import { APPLY_CUSTOM_PRICING_FORM } from './ServicesRightContent';

import {
  // calculateMinNumberOfDevices,
  getCheckboxState,
  getCustomPricing,
  getInitialTotalFeeValue,
  handleNumberOfDevicesChange,
  handleTotalFeeChange,
  isServicesChanged,
  isVisible,
  removeProductMap,
  getSelectProductCode,
  isPosTerminalService,
} from './utils';
import WebsiteConfiguration from './WebsiteConfiguration';
import Alert from '../../Alert';
// import { useScrollToTop } from '~/hooks/useScrollToTop';
import PaymentMethodsFields from '~/components/views/ServicesView/PaymentMethodsFields';
import { useTranslation } from 'react-i18next';
import { useGetDraft } from '~/controllers/draft';
import { calculateTotalPrice, selectProducts } from '~/api/pricing';
import { useDispatch } from 'react-redux';
import { getStagePayload } from '~/utils/getStagePayload';
import { FallOutlined } from '@ant-design/icons';
import { SaudiReyalSymbolIcon } from '~/img';
import { useDirection } from '~/hooks/i18nHooks';
import { isAgent } from '~/utils/getDecodedJwt';
import { getTrackingId, handleGetutm } from '~/utils/handleGetUtm';
import { useHistory } from 'react-router';
import paths from '~/pages/paths';
import PosFeesAndCharges from './PosFeesAndCharges';

const CALCULATE_TOTAL_PRICE_DEBOUNCE_MS = 1000;

type PanelErrorType = Record<ServicesKeys, boolean>;
const initialPanelErrorState: PanelErrorType = {
  nGeniusPos: false,
  eCommerce: false,
  nGeniusGo: false,
  // nGeniusONEPayByLink: false,
  // nGeniusONEPayByQR: false,
  // nGeniusONETapToPay: false,
};

export type ServicesDraft = {
  services: Partial<ApplicationPayload['services']>;
  productMap?: ProductMap;
  additionalConfigurations?: CustomPricing;
  productMapTemp?: any; // Prop to store some temporary data
  isAgreeWithServicesTermsAndConditions: boolean;
};

interface Props {
  hideServiceSelection?: boolean;
  hideDisabledServices?: boolean;
  disabled?: boolean;
}

/**
 * Formats a fee Amount object for GTM dataLayer events.
 * - Returns 'Free' when the amount is missing or 0
 * - Returns '{currency} {value}' (e.g. 'SAR 100') when value > 0
 */
const formatFeeAmount = (amount: { value: number; currency: string } | null | undefined): string => {
  if (!amount || amount.value === 0) return 'Free';
  return `${amount.currency} ${amount.value}`;
};

const ServicesForm: FC<Props> = (props) => {
  const { disabled } = props;
  const { t, i18n } = useTranslation('ksaApplication');
  const [defaultPayByLink, setDefaultPayByLink] = useState<boolean>(false);
  const [totalFee, setTotalFee] = useState<any>({});
  /** Map of serviceKey → terminal count (for POS) */
  const [posTerminalCounts, setPosTerminalCounts] = useState<Record<string, number>>({});
  const [form] = Form.useForm<ServicesDraft>();
  const isMobile = useMediaQuery({ maxWidth: theme.breakpoints.sm });
  const servicesQuery = useGetServices();
  const fieldMap = useFieldMap();
  const applyPricingMutation = useApplyCustomPricingController(form);
  const [, handleIsSelected] = useIsAtLeastOneServiceSelected();
  const isServiceSingle = servicesQuery.data?.services.length === 1;
  const application: any = useGetApplication();
  const history = useHistory();
  const isResubmit = application.data?.payload?.salesforceApplicationNumber;
  const predefineProducts = application.data?.payload?.products;
  const dir = useDirection();
  // Validation
  const [isPanelError, setPanelError] = useState<PanelErrorType>(initialPanelErrorState);
  const onFinishFailed: FormProps['onFinishFailed'] = ({ errorFields }) => {
    const result = produce(initialPanelErrorState, (draft) => {
      errorFields.forEach(({ name }) => {
        const serviceKey = name[1] as ServicesKeys;
        draft[serviceKey] = true;
      });
    });
    setPanelError(result);
  };

  function getInitialFees() {
    const products: any = {
      selectedProducts: {
        nGeniusPos: [{ key: 'terminalFee', input: 1 }],
        eCommerce: [],
        nGeniusONEPayByLink: [],
        nGeniusGo: [],
      },
    };
    calculateTotalPrice(products).then((res) => {
      setTotalFee(res?.pricing);
    });
  }
  useEffect(() => {
    getInitialFees();
  }, []);

  const { data, isLoading }: any = useGetDraft('services');

  // Total price calculation
  const totalPriceController = useTotalPriceController();

  const debouncedRecalculate = useDebouncedCallback((formValues: ServicesDraft) => {
    totalPriceController.mutate(formValues);
  }, CALCULATE_TOTAL_PRICE_DEBOUNCE_MS);

  useEffect(() => {
    if (defaultPayByLink) {
      const formValues = form.getFieldsValue();
      set(formValues, ['services', 'nGeniusONEPayByLink', 'enabled'], false);
      form.setFieldsValue(formValues);
    }
  }, [defaultPayByLink]);

  const isInitialSyncDone = useRef(false);

  useEffect(() => {
    if (isInitialSyncDone.current) return;
    const services = form.getFieldValue('services') || application.data?.payload?.services;
    if (services && !isEmpty(services)) {
      const formValues = { ...form.getFieldsValue(), services };
      handleIsSelected(formValues);
      debouncedRecalculate(formValues);
      isInitialSyncDone.current = true;
    }
  }, [application.data?.payload?.services, form, handleIsSelected, debouncedRecalculate]);

  const handleValuesChange = useMutableCallback((changedValues) => {
    // need to wait till form items will be rendered and form state become consistent

    setTimeout(() => {
      const allFormValues = form.getFieldsValue();

      handleTotalFeeChange(form)(changedValues, allFormValues);
      handleNumberOfDevicesChange(form)(changedValues, allFormValues);
    }, 0);

    // Reset terminal count to 1 when a POS service is deselected
    const changedServices = changedValues?.services as Record<string, any> | undefined;
    if (changedServices) {
      Object.entries(changedServices).forEach(([key, service]) => {
        if (service?.enabled === false && isPosTerminalService(key, form.getFieldValue(['services', key, 'type']))) {
          // Reset posTerminalCounts state
          setPosTerminalCounts((prev) => ({ ...prev, [key]: 1 }));
          // Reset form fields
          form.setFieldValue(['services', key, 'terminals'], 1);
        }
      });
    }

    //
    if (!isServicesChanged(changedValues)) return;
    if (isPanelError !== initialPanelErrorState) {
      setPanelError(initialPanelErrorState);
    }
    // need to wait till form items will be rendered and form state become consistent
    setTimeout(() => {
      const allFormValues = form.getFieldsValue();
      handleIsSelected(allFormValues);
      debouncedRecalculate(allFormValues);
    }, 0);
  });

  const [, setPricingRequestDrawerOpen] = useCustomPricingDrawerOpen();

  const handlePricingRequestDrawer = () => {
    setPricingRequestDrawerOpen(true);
    return Promise.resolve();
  };

  const savePricing = (): Promise<any> => {
    return applyPricingMutation.mutateAsync();
  };

  /**
   * Submits selected products to the backend API.
   * 
   * This function:
   * 1. Synchronizes terminal counts for all active POS services into a values object copy.
   * 2. Saves a copy of these updated values to session storage for draft persistence.
   * 3. Constructs the final products payload (mapping to API product codes and terminal counts) and submits it.
   */
  const submitSelectedProducts = async (values: any) => {
    // 1. Prepare values with terminal counts synchronized
    const valuesWithTerminals = produce(removeProductMap(values), (draft) => {
      const services = (draft.services as Record<string, any>) || {};
      for (const [key, service] of Object.entries(services)) {
        if (service?.enabled && isPosTerminalService(key, service.type)) {
          service.terminals = service.terminals ?? posTerminalCounts[key] ?? 1;
        }
      }
    });

    // 2. Persist configured services/terminal values in session storage
    sessionStorage.setItem('ksa-services-submit-values', JSON.stringify(valuesWithTerminals));

    // 3. Extract currently selected products from values and map to backend API format
    const liveServices = form.getFieldsValue()?.services as Record<string, any> | undefined;
    const servicesMap = (values?.services as Record<string, any>) || {};

    const productPayload = Object.entries(servicesMap)
      .filter(([, service]) => service?.enabled)
      .map(([key, service]) => {
        const productCode = getSelectProductCode(key, service?.type);
        const hasTerminals = isPosTerminalService(key, service?.type);

        const terminalCount = hasTerminals
          ? Number(liveServices?.[key]?.terminals ?? service?.terminals ?? posTerminalCounts[key] ?? 1)
          : undefined;

        return {
          productCode,
          selected: true,
          ...(terminalCount !== undefined ? { terminals: terminalCount } : {}),
        };
      });

    // 4. Send selectProducts payload to API if there are selected items
    if (productPayload.length > 0) {
      await selectProducts({ products: productPayload });
    }
  };

  /**
   * Pre-submission handler executed before the form is finalized.
   * Ensures pricing updates are stored and products selected are submitted to the API.
   */
  const handleBeforeFinish = async (values: any) => {
    await savePricing();
    try {
      await submitSelectedProducts(values);
    } catch (err) {
      void message.error(t('Something went wrong. Please try again.'));
      return Promise.reject(new Error('selectProducts failed'));
    }
  };

  const onFinishHandler = async (values: any) => {
    const selectedProducts = Object?.entries(values?.services || {})?.filter(
      ([key, value]: any) => {
        return value?.enabled;
      }
    );

    // Build per-product pricing for all selected services (POS, e-commerce, etc.)
    const selectedPricing = selectedProducts.map(([key, value]: any) => {
      const serviceData = servicesQuery.data?.services?.find((item: any) => item.key === key);
      const setupFeeAmount = (
        serviceData?.fields?.find(
          (fee: any) => fee.key === 'setupFee' && fee.type === 'pricing'
        ) as PricingServiceField | undefined
      )?.value.defaultValue.amount;
      const annualFeeAmount = (
        serviceData?.fields?.find((fee: any) => fee.key === 'annualFee') as
        | PricingServiceField
        | undefined
      )?.value.defaultValue.amount;
      return {
        key,
        type: value?.type,
        setupFee: formatFeeAmount(setupFeeAmount),
        annualFee: formatFeeAmount(annualFeeAmount),
      };
    });

    (window as any).dataLayer.push({
      event: 'event_formsubmit',
      page_ln: i18n.language?.slice(0, 2) || 'en',
      page_name: window.location.href,
      page_source: document.referrer || 'direct',
      product_id: selectedPricing.map((p) => p.type).join(',') || '',
      product_name: selectedPricing.map((p) => p.key).join(',') || '',
      price_one_time: selectedPricing.map((p) => p.setupFee).join(',') || 'Free',
      price_annual: selectedPricing.map((p) => p.annualFee).join(',') || 'Free',
      page_section: 'Products',
      trackingId: getTrackingId(application?.data),
      user_id: localStorage.getItem('sob_temp_uid'),
    });

    console.log('Event Pushed', (window as any).dataLayer);

    // Navigate to Start in Minutes — submitStep will be called from there
    const utmParams = handleGetutm(application?.data);
    history.push(`${paths.ksa.startInMinutes}?${utmParams}`);
  };

  if (!servicesQuery.data) return null;
  if (!fieldMap) return null;

  return (
    <ServicesContextProvider>
      <Container isMobile={isMobile}>
        <SaveWizardStep
          name={APPLY_CUSTOM_PRICING_FORM}
          mainForm={form}
          onBeforeSave={applyPricingMutation.mutateAsync}
          parseBeforeSave={removeProductMap}
          onAfterSave={() => {
            void message.success(t('Saved', { ns: 'common' }));
            return Promise.resolve();
          }}
        />
        <SaveWizardStep
          name={SAVE_STEP_FORM}
          mainForm={form}
          onBeforeSave={savePricing}
          onAfterSave={handlePricingRequestDrawer}
          parseBeforeSave={removeProductMap}
        />
        <Form<ServicesDraft>
          disabled={disabled}
          name={servicesFormName}
          stepKey={servicesFormName}
          form={form}
          payloadKey={servicesStepKey}
          onFinish={onFinishHandler}
          onBeforeFinish={handleBeforeFinish}
          onValuesChange={handleValuesChange}
          onFinishFailed={onFinishFailed}
          handleDraftDataBeforeSave={removeProductMap}
          shouldSaveDraft={isServicesChanged}
          parseBeforeSubmit={(values) => {
            return removeProductMap({
              ...values,
            });
          }}
          setInitialValues={(initialValues) => {
            const newInitialValues = produce(initialValues, (draft) => {
              // Normalize backend-persisted service keys (e.g. "POS") to the FE
              // catalog keys (e.g. "nGeniusPos") so a saved selection prefills the
              // correct product card when the products page is reopened or revisited
              // from a later stage (e.g. business details).
              const persistedServices = get(draft, ['services']) as
                | Record<string, any>
                | undefined;
              if (persistedServices && !isEmpty(persistedServices)) {
                const catalogKeys = new Set(
                  servicesQuery.data.services.map((s: any) => s.key)
                );
                Object.keys(persistedServices).forEach((persistedKey) => {
                  if (catalogKeys.has(persistedKey)) return;
                  const persistedValue = persistedServices[persistedKey];
                  const productCode = getSelectProductCode(
                    persistedKey,
                    persistedValue?.type
                  );
                  const target = servicesQuery.data.services.find(
                    (s: any) => getSelectProductCode(s.key, s.type) === productCode
                  );
                  if (target && target.key !== persistedKey && !persistedServices[target.key]) {
                    set(draft, ['services', target.key], {
                      ...persistedValue,
                      enabled: true,
                    });
                    unset(draft, ['services', persistedKey]);
                  }
                });
              }
              sortBy(servicesQuery.data.services, 'numberInQueue').forEach((item) => {
                const serviceVal = get(draft, ['services', item.key]);
                if (serviceVal) {
                  const count = serviceVal.terminals ?? serviceVal.terminalFee ?? 1;
                  serviceVal.terminals = count;
                  serviceVal.terminalFee = count;
                }
                item.fields.forEach((fee) => {
                  if (
                    typeof get(draft, ['services', item.key, fee.key]) !== 'number' &&
                    fee.type !== 'composite' &&
                    fee.type !== 'schema' &&
                    fee.requiresNumber
                  ) {
                    const pricingFee = fee as PricingServiceField;
                    // set(draft, ['services', item.key, fee.key], calculateMinNumberOfDevices(pricingFee));
                  }
                });
                set(draft, ['services', item.key, 'type'], item.type);

                if (isServiceSingle) {
                  set(draft, ['services', item.key, 'enabled'], true);
                }

                set(
                  draft,
                  ['productMap', item.key],
                  getCustomPricing({
                    fields: item.fields,
                    initialValues: get(draft, ['productMap', item.key]),
                  })
                );

                set(
                  draft,
                  ['productMapTemp', item.key, 'terminalFee'],
                  getInitialTotalFeeValue({
                    serviceKey: item.key,
                    allValues: draft,
                  })
                );
                if (isEmpty(initialValues.services) && predefineProducts?.[item.type]) {
                  set(draft, ['services', item.key, 'enabled'], true);
                }
              });

              if (servicesQuery.data.additionalConfigurations) {
                const fields = servicesQuery.data.additionalConfigurations;
                set(
                  draft,
                  ['additionalConfigurations'],
                  getCustomPricing({
                    fields,
                    initialValues: get(draft, ['additionalConfigurations']),
                  })
                );
              }
            });
            return newInitialValues;
          }}
        >
          <Space direction="vertical" size={0}>
            {sortBy(servicesQuery.data.services, 'numberInQueue').map((item) => {
              return (
                <Form.ShouldUpdate
                  key={item.key}
                  shouldUpdate={(prev, next) =>
                    Object.entries(prev.services || {}).filter(([, s]: any[]) => s.enabled)
                      .length !==
                    Object.entries(next.services || {}).filter(([, s]: any[]) => s.enabled).length
                  }
                >
                  {() => {
                    const formValues = form.getFieldsValue();
                    const { enabled, isCheckboxDisabled, numberOfDevicesInput } = getCheckboxState(
                      formValues,
                      item
                    );

                    // const isPayByLinkSelectedByDefault = isPayByLinkSelectedByDefaultHandler(formValues?.services);
                    // setDefaultPayByLink(isPayByLinkSelectedByDefault);
                    const isPosTerminal = isPosTerminalService(item.key, item.type);
                    const terminalCount =
                      formValues?.services?.[item.key]?.terminals ??
                      posTerminalCounts[item.key] ??
                      1;
                    return (
                      <Space size={0} direction="vertical">
                        <Form.Item
                          name={['services', item.key, 'type']}
                          initialValue={item?.type}
                          hidden
                        />
                        {isPosTerminal && (
                          <>
                            <Form.Item
                              name={['services', item.key, 'terminals']}
                              initialValue={1}
                              hidden
                            />
                            <Form.Item
                              name={['services', item.key, 'terminalFee']}
                              initialValue={1}
                              hidden
                            />
                          </>
                        )}
                        <Form.Item
                          initialValue={false}
                          name={['services', item.key, 'enabled']}
                          valuePropName="checked"
                          disabled={isCheckboxDisabled || isAgent()}
                          noStyle
                        >
                          <ServiceCheckbox
                            feesAndCharges={
                              <PosFeesAndCharges fields={item.fields} compact />
                            }
                            type={item.type}
                            imageUrl={item.imageUrl}
                            initialPage={false}
                            inputKey={item.key}
                            name={item.name}
                            longDescription={item.longDescription}
                            shortDescriptionText={isPosTerminal ? 'POS.short' : undefined}
                            setupFee={
                              formatFeeAmount(
                                (
                                  item.fields.find(
                                    (fee) => fee.key === 'setupFee' && fee.type === 'pricing'
                                  ) as PricingServiceField | undefined
                                )?.value.defaultValue.amount
                              )
                            }
                            annualFee={
                              formatFeeAmount(
                                item.key === 'nGeniusPos' || item.key === 'nGeniusGo'
                                  ? (
                                    item.fields.find(
                                      (fee) => fee.key === 'terminalFee' && fee.type === 'pricing'
                                    ) as PricingServiceField | undefined
                                  )?.value.defaultValue.amount
                                  : (
                                    item.fields.find((fee) => fee.key === 'annualFee') as
                                    | PricingServiceField
                                    | undefined
                                  )?.value.defaultValue.amount
                              )
                            }
                            totalFee={totalFee[item.name]?.amount?.value}
                            header={item.header}
                            disable={isCheckboxDisabled || !!isResubmit || isAgent()}
                            maxTerminals={
                              isPosTerminal
                                ? parseInt(
                                  (item.fields.find(
                                    (f) => f.key === 'maxTerminals' && f.type === 'string'
                                  ) as any)?.value ??
                                  (item.fields.find(
                                    (f) => f.key === 'maxTerminals' && f.type === 'string'
                                  ) as any)?.defaultValue ??
                                  '2',
                                  10
                                )
                                : undefined
                            }
                            terminalCount={isPosTerminal ? terminalCount : undefined}
                            onTerminalCountChange={
                              isPosTerminal
                                ? (count) => {
                                  setPosTerminalCounts((prev) => ({ ...prev, [item.key]: count }));
                                  form.setFieldValue(['services', item.key, 'terminals'], count);
                                  form.setFieldValue(['services', item.key, 'terminalFee'], count);
                                  handleValuesChange(
                                    {
                                      services: {
                                        [item.key]: {
                                          terminals: count,
                                          terminalFee: count,
                                        },
                                      },
                                    }
                                  );
                                }
                                : undefined
                            }
                          />
                        </Form.Item>
                      </Space>
                    );
                  }}
                </Form.ShouldUpdate>
              );
            })}
          </Space>
        </Form>
      </Container>
    </ServicesContextProvider>
  );
};

const Container = styled.div<any>`
  margin-top: ${({ isMobile }) => (isMobile ? '-40px' : '-24px')};
  .ServicesForm-Collapse {
    .Collapse-Icon {
      vertical-align: 2.5px !important;
    }
    .ant-collapse-header {
      font-size: 24px;
      padding: 16px 0;
      text-transform: unset;
    }
    .ant-collapse-content-box {
      padding: 0 0 40px 0 !important;
    }
  }
  .ServicesForm-Space {
    flex-wrap: wrap;
    position: sticky;
    top: 0;
    background-color: ${colors.componentBackground};
    z-index: 3;
    margin-bottom: 40px;
    align-items: flex-start;
    padding: 20px;
    border-radius: 14px;
    box-shadow: 0px 2px 2px 1px ${colors.componentBackground};
    .ant-space-item {
      display: flex;
      flex: 1;
      height: 100%;
    }
  }
  .ServicesForm-Title {
    margin-top: 0 !important;
    margin-bottom: 16px;
  }
  .ServicesForm-CardMobile {
    margin-bottom: 24px;
    padding-bottom: 20px;
  }
  margin-bottom: ${({ isMobile }) => (isMobile ? '130px' : undefined)};
`;

export default ServicesForm;
